import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Key, Copy, Check, ExternalLink, Zap, Download, CreditCard, RefreshCw, Settings, AlertTriangle, BarChart3, Shield, Users, Coins, Sparkles, X, Play, Lock, Globe , Award} from 'lucide-react';
import ReferralSection from './ReferralSection';
import WhiteLabelConfig from './WhiteLabelConfig';
import APIKeysManager from './APIKeysManager';
import AnalyticsDashboard from './AnalyticsDashboard';
import AgencyManager from './AgencyManager';
import WalletPanel from './WalletPanel';
import AEOPanel from './AEOPanel';
import UserDashboardFoundersSection from './UserDashboardFoundersSection';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LEVELS = { trial: 0, starter: 0, professional: 1, team: 2, enterprise: 3 };
const TIER_NAMES = { 0: 'Starter', 1: 'Professional', 2: 'Team', 3: 'Enterprise' };
const TAB_TIER = { overview: 0, aeo: 2, wallet: 0, analytics: 1, apikeys: 2, whitelabel: 2, agency: 3 };

const getUserMaxTier = (licenses) => {
  if (!licenses || licenses.length === 0) return 0;
  const activeLicenses = licenses.filter(l => l.status === 'active');
  if (activeLicenses.length === 0) return 0;
  return Math.max(...activeLicenses.map(l => TIER_LEVELS[l.type] ?? TIER_LEVELS[l.package_id] ?? 0));
};

const UserDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pluginValidation, setPluginValidation] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [autoRefill, setAutoRefill] = useState({ enabled: false, threshold: 500, refill_package: 'subscription_basic' });
  const [autoRefillLoading, setAutoRefillLoading] = useState(false);
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [wallet, setWallet] = useState(null); // 3.5.3 — wallet plan + topup
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [welcomeBannerVisible, setWelcomeBannerVisible] = useState(() => {
    return localStorage.getItem('welcome_banner_dismissed') !== 'true';
  });
  const [bannerFading, setBannerFading] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const dismissWelcomeBanner = () => {
    setBannerFading(true);
    setTimeout(() => {
      setWelcomeBannerVisible(false);
      localStorage.setItem('welcome_banner_dismissed', 'true');
    }, 300);
  };

  const fetchLicenses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/licenses`, authHeaders);
      setLicenses(res.data);
    } catch (err) {
      console.error('Failed to fetch licenses:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAutoRefill = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/billing/auto-refill`, authHeaders);
      setAutoRefill(res.data);
    } catch (err) {
      console.error('Failed to fetch auto-refill:', err);
    }
  }, [token]);

  const fetchCreditsInfo = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/billing/credits/check-low`, {}, authHeaders);
      setCreditsInfo(res.data);
    } catch (err) {
      console.error('Failed to check credits:', err);
    }
  }, [token]);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/wallet/balance`, authHeaders);
      setWallet(res.data);
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
    }
  }, [token]);

  const fetchPluginValidation = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/plugin/validate-e2e`, {}, authHeaders);
      setPluginValidation(res.data);
    } catch (err) {
      console.error('Plugin validation error:', err);
    }
  }, [token]);

  const handleDownloadPlugin = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/plugin/download`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'orchestra-plugin.zip';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.status === 403 ? t('dashboard.dlError403') : t('dashboard.dlGenericError');
      alert(msg);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
    fetchAutoRefill();
    fetchCreditsInfo();
    fetchWallet();
    fetchPluginValidation();
  }, [fetchLicenses, fetchAutoRefill, fetchCreditsInfo, fetchWallet]);

  const copyToClipboard = async (key) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(key);
      } else {
        const ta = document.createElement('textarea');
        ta.value = key;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      window.prompt('Copia:', key);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const localeMap = { it: 'it-IT', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE' };
    const lng = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(lng, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/billing/portal`, {}, authHeaders);
      if (res.data.url) {
        window.open(res.data.url, '_blank');
      } else {
        alert(res.data.message || t('dashboard.portalErrorAlert'));
      }
    } catch (err) {
      console.error('Failed to open portal:', err);
      alert(t('dashboard.portalErrorAlert'));
    } finally {
      setPortalLoading(false);
    }
  };

  const toggleAutoRefill = async () => {
    setAutoRefillLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/billing/auto-refill`, {
        enabled: !autoRefill.enabled,
        threshold: autoRefill.threshold || 500,
        refill_package: autoRefill.refill_package || 'subscription_basic'
      }, authHeaders);
      if (res.data.success) {
        setAutoRefill(prev => ({ ...prev, enabled: !prev.enabled }));
      } else if (res.data.redirect) {
        openCustomerPortal();
      }
    } catch (err) {
      console.error('Failed to toggle auto-refill:', err);
    } finally {
      setAutoRefillLoading(false);
    }
  };

  const totalCredits = creditsInfo?.total_credits ?? 0;
  const isLowCredits = creditsInfo?.is_low ?? false;
  const userMaxTier = getUserMaxTier(licenses);
  const hasB2bLicense = licenses.some(l => l.status === 'active' && (l.package_id === 'b2b_custom' || l.package_name === 'B2B White-Label'));

  const handleTabClick = (tabId, requiredTier) => {
    if (userMaxTier >= requiredTier) {
      setActiveTab(tabId);
    } else {
      setUpgradeModal(TIER_NAMES[requiredTier]);
    }
  };

  return (
    <div className="min-h-screen pt-24 px-6 pb-24 sm:pb-12" data-testid="user-dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-['Outfit'] text-4xl font-medium mb-2">{t('dashboard.title')}</h1>
          <p className="text-[#A1A1AA]">{t('dashboard.subtitle')}</p>
        </div>

        {/* User Info Card */}
        <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#0055FF]/20 flex items-center justify-center">
                <span className="text-xl font-['Outfit'] font-semibold text-[#0055FF]">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="font-['Outfit'] font-medium text-lg">{user?.company_name || t('dashboard.userCard.defaultName')}</h3>
                <p className="text-[#A1A1AA] text-sm">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user?.referral_credits > 0 && (
                <div className="text-right">
                  <span className="text-xs text-[#A1A1AA]">{t('dashboard.userCard.referralCredits')}</span>
                  <div className="text-xl font-['Outfit'] font-semibold text-[#F59E0B]">{user.referral_credits}</div>
                </div>
              )}
              <div className="text-right">
                <span className="text-xs text-[#A1A1AA]">{t('dashboard.userCard.aiCredits')}</span>
                <div className={`text-xl font-['Outfit'] font-semibold ${isLowCredits ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                  {totalCredits}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Verification Banner */}
        {user && !user.email_verified && (
          <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-2xl p-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="email-verification-banner">
            <AlertTriangle className="w-6 h-6 text-[#F59E0B] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#F59E0B]">{t('dashboard.emailVerify.title')}</p>
              <p className="text-xs text-[#A1A1AA]">{t('dashboard.emailVerify.body')}</p>
            </div>
            <Button
              size="sm"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-black text-xs"
              data-testid="resend-verification-btn"
              onClick={async () => {
                try {
                  await axios.post(`${API_URL}/api/auth/resend-verification`, { email: user.email }, authHeaders);
                  alert(t('dashboard.emailVerify.sentAlert'));
                } catch {
                  alert(t('dashboard.emailVerify.errorAlert'));
                }
              }}
            >
              {t('dashboard.emailVerify.resend')}
            </Button>
          </div>
        )}

        {/* Low Credits Warning */}
        {isLowCredits && (
          <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-2xl p-4 mb-8 flex items-center gap-4" data-testid="low-credits-warning">
            <AlertTriangle className="w-6 h-6 text-[#EF4444] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">{t('dashboard.lowCredits.title')}</p>
              <p className="text-xs text-[#A1A1AA]">{t('dashboard.lowCredits.body', { credits: totalCredits })}</p>
            </div>
            <a href="/pricing">
              <Button size="sm" className="bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs" data-testid="refill-credits-btn">
                {t('dashboard.lowCredits.refillBtn')}
              </Button>
            </a>
          </div>
        )}

        {/* Billing Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <button
            onClick={openCustomerPortal}
            disabled={portalLoading}
            className="bg-[#111111] border border-white/10 rounded-2xl p-5 text-left hover:border-[#0055FF]/50 transition-colors group"
            data-testid="manage-subscription-btn"
          >
            <CreditCard className="w-6 h-6 text-[#0055FF] mb-3 group-hover:scale-110 transition-transform" />
            <h4 className="font-['Outfit'] font-medium text-sm mb-1">{t('dashboard.quickActions.subscription.title')}</h4>
            <p className="text-xs text-[#A1A1AA]">
              {portalLoading ? t('dashboard.quickActions.subscription.opening') : t('dashboard.quickActions.subscription.desc')}
            </p>
          </button>

          <button
            onClick={toggleAutoRefill}
            disabled={autoRefillLoading}
            className="bg-[#111111] border border-white/10 rounded-2xl p-5 text-left hover:border-[#10B981]/50 transition-colors group"
            data-testid="auto-refill-toggle-btn"
          >
            <RefreshCw className={`w-6 h-6 mb-3 transition-transform group-hover:scale-110 ${autoRefill.enabled ? 'text-[#10B981]' : 'text-[#A1A1AA]'}`} />
            <h4 className="font-['Outfit'] font-medium text-sm mb-1">
              {t('dashboard.quickActions.autoRefill.title')} {autoRefill.enabled ? <span className="text-[#10B981]">ON</span> : <span className="text-[#A1A1AA]">OFF</span>}
            </h4>
            <p className="text-xs text-[#A1A1AA]">
              {autoRefillLoading ? t('dashboard.quickActions.autoRefill.updating') : t('dashboard.quickActions.autoRefill.descBelow', { threshold: autoRefill.threshold })}
            </p>
          </button>

          <a href="/pricing" className="block">
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 text-left hover:border-[#F59E0B]/50 transition-colors group h-full">
              <Settings className="w-6 h-6 text-[#F59E0B] mb-3 group-hover:scale-110 transition-transform" />
              <h4 className="font-['Outfit'] font-medium text-sm mb-1">{t('dashboard.quickActions.upgrade.title')}</h4>
              <p className="text-xs text-[#A1A1AA]">{t('dashboard.quickActions.upgrade.desc')}</p>
            </div>
          </a>
        </div>

        {/* Upgrade Modal */}
        {upgradeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setUpgradeModal(null)} data-testid="upgrade-modal-overlay">
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-4 text-center" onClick={e => e.stopPropagation()} data-testid="upgrade-modal">
              <div className="w-14 h-14 rounded-full bg-[#F59E0B]/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[#F59E0B]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold mb-2">{t('dashboard.upgradeModal.title')}</h3>
              <p className="text-[#A1A1AA] text-sm mb-6" dangerouslySetInnerHTML={{ __html: t('dashboard.upgradeModal.body', { tier: upgradeModal }) }} />
              <div className="flex gap-3 justify-center">
                <a href="/pricing">
                  <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-6" data-testid="upgrade-modal-cta">
                    {t('dashboard.upgradeModal.seePlans')}
                  </Button>
                </a>
                <Button variant="outline" className="border-white/10 text-[#A1A1AA] hover:text-white rounded-full px-6" onClick={() => setUpgradeModal(null)} data-testid="upgrade-modal-close">
                  {t('dashboard.upgradeModal.close')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-8 bg-[#111111] rounded-xl p-1 border border-white/5 overflow-x-auto">
          {[
            { id: 'overview',  label: t('dashboard.tabs.licenses'),   icon: Key },
            { id: 'aeo',       label: t('dashboard.tabs.competitor'), icon: Sparkles },
            { id: 'wallet',    label: t('dashboard.tabs.credits'),    icon: Coins },
            { id: 'analytics', label: t('dashboard.tabs.analytics'),  icon: BarChart3 },
            { id: 'apikeys',   label: t('dashboard.tabs.apiKeys'),    icon: Shield },
            ...(hasB2bLicense ? [{ id: 'whitelabel', label: t('dashboard.tabs.whiteLabel'), icon: Settings }] : []),
            ...(licenses.some(l => l.source === 'ltd_redemption') ? [{ id: 'founders', label: 'Founders', icon: Award }] : []),
            { id: 'agency',    label: t('dashboard.tabs.agency'),     icon: Users }
          ].map(tab => {
            const requiredTier = TAB_TIER[tab.id] ?? 0;
            const isLocked = userMaxTier < requiredTier;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id, requiredTier)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-all whitespace-nowrap ${
                  isLocked
                    ? 'opacity-40 cursor-not-allowed text-[#A1A1AA]'
                    : activeTab === tab.id
                      ? 'bg-[#0055FF] text-white'
                      : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {isLocked ? <Lock className="w-4 h-4" /> : <tab.icon className="w-4 h-4" />}
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Licenses */}
          <div className="lg:col-span-2">
            {/* Welcome Banner */}
            {welcomeBannerVisible && (
              <div
                data-testid="welcome-banner"
                className={`relative bg-[#1e3a5f] border border-[#3b82f6] rounded-xl p-5 mb-6 transition-opacity duration-300 ${bannerFading ? 'opacity-0' : 'opacity-100'}`}
              >
                <button
                  onClick={dismissWelcomeBanner}
                  className="absolute top-3 right-3 text-[#9ca3af] hover:text-white transition-colors"
                  data-testid="welcome-banner-close"
                >
                  <X className="w-4 h-4" />
                </button>
                <h3 className="font-['Outfit'] font-semibold text-white text-base mb-2">{t('dashboard.welcome.title')}</h3>
                <p className="text-[#94a3b8] text-sm mb-3">{t('dashboard.welcome.lead')}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#3b82f6] text-white text-xs flex items-center justify-center font-bold mt-0.5">1</span>
                    <span className="text-sm text-[#e2e8f0]" dangerouslySetInnerHTML={{ __html: t('dashboard.welcome.step1') }} />
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#3b82f6] text-white text-xs flex items-center justify-center font-bold mt-0.5">2</span>
                    <span className="text-sm text-[#e2e8f0]">{t('dashboard.welcome.step2')}</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#3b82f6] text-white text-xs flex items-center justify-center font-bold mt-0.5">3</span>
                    <span className="text-sm text-[#e2e8f0]">{t('dashboard.welcome.step3')}</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    className="bg-[#3b82f6] hover:bg-[#2563eb] text-white text-sm rounded-lg px-4 py-2"
                    data-testid="welcome-banner-video-btn"
                    onClick={() => window.open('#', '_blank')}
                  >
                    <Play className="w-3.5 h-3.5 mr-1.5" /> {t('dashboard.welcome.video')}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#3b82f6]/50 text-[#93c5fd] hover:bg-[#3b82f6]/10 text-sm rounded-lg px-4 py-2"
                    data-testid="welcome-banner-dismiss-btn"
                    onClick={dismissWelcomeBanner}
                  >
                    {t('dashboard.welcome.dismiss')}
                  </Button>
                </div>
              </div>
            )}

            <h2 className="font-['Outfit'] text-lg font-medium mb-4">{t('dashboard.licenses.heading')}</h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-[#A1A1AA] mt-4">{t('dashboard.licenses.loading')}</p>
              </div>
            ) : licenses.length === 0 ? (
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-12 text-center">
                <Key className="w-16 h-16 text-[#A1A1AA] mx-auto mb-4" />
                <h3 className="font-['Outfit'] text-xl font-medium mb-2">{t('dashboard.licenses.noneTitle')}</h3>
                <p className="text-[#A1A1AA] mb-6">{t('dashboard.licenses.noneSubtitle')}</p>
                <a href="/pricing">
                  <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8" data-testid="buy-license-btn">
                    {t('dashboard.licenses.buyBtn')}
                  </Button>
                </a>
              </div>
            ) : (
              <div className="space-y-6">
                {licenses.map((license) => (
                  <div key={license.id} className="bg-[#111111] border border-white/10 rounded-2xl p-6" data-testid={`license-card-${license.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <Zap className="w-5 h-5 text-[#0055FF]" />
                          <h3 className="font-['Outfit'] font-medium text-lg">{license.package_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${license.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                            {license.status === 'active' ? t('dashboard.licenses.active') : t('dashboard.licenses.expired')}
                          </span>
                        </div>
                        <p className="text-[#A1A1AA] text-sm">{t('dashboard.licenses.createdOn', { date: formatDate(license.created_at) })}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={handleDownloadPlugin}
                          disabled={downloading || license.status !== 'active'}
                          className="bg-[#0055FF] hover:bg-[#3377FF] text-white text-sm rounded-full px-4"
                          data-testid={`download-plugin-${license.id}`}
                        >
                          {downloading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                          {t('dashboard.licenses.downloadBtn')}
                        </Button>
                      </div>
                    </div>

                    {/* Plugin Download Info */}
                    <div className="bg-gradient-to-r from-[#0055FF]/5 to-transparent border border-[#0055FF]/20 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <Download className="w-5 h-5 text-[#0055FF] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium mb-2">{t('dashboard.licenses.pluginInfo.title')}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div>
                              <span className="text-[#A1A1AA] block">License Key</span>
                              <span className={pluginValidation?.license_active ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                                {pluginValidation?.license_active ? t('dashboard.licenses.pluginInfo.preconfigured') : t('dashboard.licenses.pluginInfo.missing')}
                              </span>
                            </div>
                            {hasB2bLicense && (
                            <div>
                              <span className="text-[#A1A1AA] block">White-Label</span>
                              <span className={pluginValidation?.whitelabel_configured ? 'text-[#10B981]' : 'text-[#F59E0B]'}>
                                {pluginValidation?.whitelabel_configured ? (pluginValidation.whitelabel_company || t('dashboard.licenses.pluginInfo.configured')) : t('dashboard.licenses.pluginInfo.defaultLabel')}
                              </span>
                            </div>
                            )}
                            <div>
                              <span className="text-[#A1A1AA] block">{t('dashboard.licenses.pluginInfo.aiEngine')}</span>
                              <span className={pluginValidation?.ai_service_ready ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                                {pluginValidation?.ai_service_ready ? t('dashboard.licenses.pluginInfo.aiActive') : t('dashboard.licenses.pluginInfo.aiUnavailable')}
                              </span>
                            </div>
                            <div>
                              <span className="text-[#A1A1AA] block">{t('dashboard.licenses.pluginInfo.creditsRemaining')}</span>
                              {/* D5 (lancio): crediti dal wallet (credits_remaining), mai dal
                                  contatore chiamate — api_calls_remaining non è a listino. */}
                              <span className="text-white">{pluginValidation?.credits_remaining?.toLocaleString() || '—'}</span>
                            </div>
                          </div>
                          <p className="text-[10px] text-[#A1A1AA] mt-2">{t('dashboard.licenses.pluginInfo.uploadHint')}</p>
                        </div>
                      </div>
                    </div>

                    {/* License Key */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <span className="text-xs text-[#A1A1AA] block mb-1">License Key</span>
                          <code className="font-mono text-[#00E5FF] text-sm break-all">{license.license_key}</code>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(license.license_key)} className="text-[#A1A1AA] hover:text-white flex-shrink-0" data-testid={`copy-license-key-${license.id}`}>
                          {copiedKey === license.license_key ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Dominio Attivo */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6 flex items-center gap-3" data-testid={`license-domain-${license.id}`}>
                      <Globe className="w-4 h-4 text-[#0055FF] flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs text-[#A1A1AA] block mb-0.5">{t('dashboard.licenses.domainActive')}</span>
                        {license.domain ? (
                          <span className="text-sm text-[#10B981] font-medium">{license.domain}</span>
                        ) : (
                          <span className="text-sm text-[#A1A1AA]">{t('dashboard.licenses.domainNone')}</span>
                        )}
                      </div>
                    </div>

                    {/* Stats with Credit Progress Bar */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white/5 rounded-xl p-4 col-span-2" data-testid={`credit-progress-${license.id}`}>
                        {(() => {
                          // 3.5.3 — Crediti dal WALLET (allineati con plugin), suddivisi piano + top-up
                          const planRemaining = wallet ? (wallet.plan_credits || 0) : null;
                          const topupRemaining = wallet ? (wallet.topup_credits || 0) : null;
                          const totalRemaining = wallet ? (planRemaining + topupRemaining) : null;
                          // Plan size: dal package definition (fallback su license.api_calls_limit)
                          const planSizes = { trial: 50, starter: 300, professional: 2000, team: 1000 };
                          const planTotalSize = planSizes[license.type] || planSizes[license.package_id] || license.api_calls_limit || 1000;
                          // Progress bar mostra il piano: quanto del piano è ancora disponibile
                          const planPct = planTotalSize > 0 && planRemaining !== null ? Math.min(100, (planRemaining / planTotalSize) * 100) : 0;
                          const barColor = planPct > 50 ? '#22c55e' : planPct > 20 ? '#eab308' : '#ef4444';
                          if (totalRemaining === null) {
                            return <span className="text-sm text-[#A1A1AA]">{t('dashboard.licenses.stats.loading')}</span>;
                          }
                          return (
                            <>
                              <span className="text-xs text-[#A1A1AA] block mb-1">{t('dashboard.licenses.stats.totalAvailable')}</span>
                              <span className="font-['Outfit'] font-semibold text-2xl" style={{ color: barColor }}>{totalRemaining.toLocaleString()}</span>
                              <div className="w-full h-1 bg-white/10 rounded-full mt-2 overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${planPct}%`, backgroundColor: barColor }} />
                              </div>
                              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                                <span className="text-[#9ca3af]">
                                  {t('dashboard.licenses.stats.planLabel')}: <strong className="text-white">{planRemaining.toLocaleString()}</strong>
                                  {planRemaining <= planTotalSize && (
                                    <span className="text-[#6b7280]"> / {planTotalSize.toLocaleString()}</span>
                                  )}
                                  {planRemaining > planTotalSize && (
                                    <span className="text-[#10B981] ml-1">({(planRemaining - planTotalSize).toLocaleString()} {t('dashboard.licenses.stats.bonus')})</span>
                                  )}
                                </span>
                                <span className="text-[#9ca3af]">
                                  {t('dashboard.licenses.stats.topupLabel')}: <strong className="text-white">{topupRemaining.toLocaleString()}</strong>
                                </span>
                              </div>
                              {planPct <= 20 && (
                                <div className="mt-1.5">
                                  <a href="?tab=wallet" className="text-[11px] text-[#ef4444] hover:underline">{t('dashboard.licenses.stats.lowPlan')}</a>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <span className="text-xs text-[#A1A1AA] block mb-1">{t('dashboard.licenses.stats.sites')}</span>
                        <span className="font-['Outfit'] font-medium">{license.domains?.length || 0}</span>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <span className="text-xs text-[#A1A1AA] block mb-1">{t('dashboard.licenses.stats.expiry')}</span>
                        <span className="font-['Outfit'] font-medium">{license.expires_at ? formatDate(license.expires_at) : t('dashboard.licenses.stats.lifetime')}</span>
                      </div>
                    </div>

                    {license.activated_domains && license.activated_domains.length > 0 ? (
                      <div className="mt-6">
                        <span className="text-xs text-[#A1A1AA] block mb-2">{t('dashboard.licenses.activatedDomains')}:</span>
                        <div className="space-y-2">
                          {license.activated_domains.filter(d => d.status === 'active').map((d, i) => {
                            const localeMap = { it: 'it-IT', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE' };
                            const lng = localeMap[i18n.language] || 'en-US';
                            return (
                            <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-2.5" data-testid={`domain-${d.domain}`}>
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-[#10B981]" />
                                <span className="text-sm font-mono">{d.domain}</span>
                                <span className="text-[10px] text-[#555]">{t('dashboard.licenses.activeFrom')} {d.activated_at ? new Date(d.activated_at).toLocaleDateString(lng) : '—'}</span>
                              </div>
                              <button
                                onClick={() => {
                                  const newDomain = prompt(t('dashboard.licenses.newDomainPrompt'));
                                  if (newDomain && newDomain.trim()) {
                                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/license/transfer?license_key=${license.license_key}&old_domain=${encodeURIComponent(d.domain)}&new_domain=${encodeURIComponent(newDomain.trim())}`, {
                                      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                    }).then(r => r.json()).then(res => {
                                      if (res.success) { alert(t('dashboard.licenses.transferred')); window.location.reload(); }
                                      else { alert(res.detail || res.message || t('dashboard.licenses.transferError')); }
                                    }).catch(() => alert(t('dashboard.licenses.connectionError')));
                                  }
                                }}
                                className="text-xs text-[#A1A1AA] hover:text-white transition-colors underline"
                                data-testid={`transfer-domain-${d.domain}`}
                              >
                                {t('dashboard.licenses.transfer')}
                              </button>
                            </div>
                            );
                          })}
                          {license.activated_domains.filter(d => d.status === 'transferred').length > 0 && (
                            <details className="mt-2">
                              <summary className="text-xs text-[#555] cursor-pointer hover:text-[#A1A1AA]">{t('dashboard.licenses.previousDomains')}</summary>
                              <div className="mt-1 space-y-1">
                                {license.activated_domains.filter(d => d.status === 'transferred').map((d, i) => (
                                  <span key={i} className="block text-xs text-[#555] line-through">{d.domain}</span>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    ) : license.domains && license.domains.length > 0 ? (
                      <div className="mt-6">
                        <span className="text-xs text-[#A1A1AA] block mb-2">{t('dashboard.licenses.linkedDomains')}:</span>
                        <div className="flex flex-wrap gap-2">
                          {license.domains.map((domain, i) => (
                            <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-sm">{domain}</span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Referral Section */}
            <ReferralSection />
          </div>
        </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* AEO Tab */}
        {activeTab === 'aeo' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <AEOPanel licenseKey={licenses[0]?.license_key || ''} />
          </div>
        )}

        {/* Wallet Tab */}
        {activeTab === 'wallet' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <WalletPanel />
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'apikeys' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <APIKeysManager />
          </div>
        )}

        {/* White-Label Tab */}
        {activeTab === 'whitelabel' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <WhiteLabelConfig />
          </div>
        )}

        {/* Founders Tab (Phase 5 — LTD redemption) */}
        {activeTab === 'founders' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <UserDashboardFoundersSection token={token} user={user} />
          </div>
        )}

        {/* Agency Tab */}
        {activeTab === 'agency' && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
            <AgencyManager />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
